import React, { ChangeEvent, useEffect, useState } from "react";
import Button from "../../components/Button/Button";
import "./HomePage.css";
import { Link, useNavigate } from "react-router-dom";
import { Tile } from "../../components/Tile/Tile";
import useAuthService from "../../auth/AuthService";
import Img1 from "../../assets/img/Fam2.png";
import mobileImg1 from "../../assets/img/home_mobile_img(1).png";
import Img2 from "../../assets/img/home_img(2).png";
import Img3 from "../../assets/img/home_img(3).jpg";
import Img4 from "../../assets/img/home_img(4).jpg";
import { GetHomeRankings } from "../../services/rankings.service";
import { IRankingsHome } from "../../types/types";
import { handlePrice } from "../../utils/formatdate";
import Tooltip from "../../components/ToolTip/ToolTip";
import Table from "../../components/Table/Table";
import InputBox from "../../components/Input/Input";
import FeedList from "../../components/FeedList/FeedList";
import NullPageComponent from "../../components/Tile/NullPageComponent";
import { parseMessage } from "../../utils/parseTemplateMessage";

interface FeedData {
  id: string;
  figId: string;
  public_id: string;
  figName: string;
  content: string;
  time: string;
}

const HomePage: React.FC = () => {
  const { isAuthenticated, signup, getUser } = useAuthService();
  const navigate = useNavigate();
  const [state, setState] = useState({
    isLoading: false,
    isNewAccount: false,
    email: "",
    feeds: [] as FeedData[],
    activeTab: "feed" as "feed" | "following",
    rankings: [] as IRankingsHome[],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }));
        const rankings = await GetHomeRankings();
        if (rankings.data !== null && rankings.error === null) {
          setState((prev) => ({ ...prev, rankings: rankings.data }));
        }
      } catch (err) {
        console.error("Error in fetching user data: ", err);
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
    //eslint-disable-next-line
  }, [isAuthenticated]);

  // WebSocket Feed
  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connectWebSocket = async () => {
      const user = getUser();
      if (!user?.sub) return console.error("User not authenticated");

      socket = new WebSocket(
        `${process.env.REACT_APP_FAN1_WEBSOCKET_URL}/api/following/GetFeeds?id=${user.sub}`
      );

      socket.onmessage = (event) => {
        try {
          const newFeeds: FeedData[] = JSON.parse(event.data);
          setState((prev) => ({
            ...prev,
            feeds: newFeeds || [],
            isLoading: false,
          }));
        } catch (error) {
          console.error("Error parsing WebSocket data:", error);
        }
      };

      socket.onerror = () => {
        console.error("WebSocket error. Reconnecting...");
        socket?.close();
        socket = null;
        if (!reconnectTimeout) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      };

      socket.onclose = () => {
        console.log("WebSocket closed. Reconnecting...");
        if (!reconnectTimeout) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      };
    };

    connectWebSocket();

    return () => {
      socket?.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [getUser]);

  const rank_columns = [
    {
      key: "rank",
      header: "",
      className: "text-center position-relative",
      render: (value: any) => <h4 className={"rank-id my-auto me-4"}>{value}</h4>,
    },
    {
      key: "entity",
      header: "",
      render: (value: any, row: any) => (
        <div className="d-flex align-items-center">
          {row.image_url ? (
            <img src={row.image_url} className="image" alt={row.entity_name} />
          ) : (
            <div
              style={{ borderRadius: "4px 4px 0px 0px" }}
              className={`default-user-img text-dark-purple bg-extra-light-purple d-flex justify-content-center align-items-center chat-image`}
            >
              <i className="fa-solid fa-user" style={{ fontSize: 20 }}></i>
            </div>
          )}
          <div className="my-auto text-start line-21 ms-3">
            <Link
              to={`/${row.public_id}`}
              state={{ id: row.entity_id }}
              className="text-decoration-none purple-color text-start font-18 font-500"
            >
              {row.entity_name}
            </Link>
          </div>
        </div>
      ),
    },
    {
      key: "countdown",
      header: "",
      render: (value: any) => (
        <div className="font-inter-500-16 pe-3">
          <Tooltip text="Time until the next auction">{value}d</Tooltip>
        </div>
      ),
    },
    {
      key: "annualized_value",
      header: "",
      render: (value: any) => (
        <div className="font-inter-500-16 me-2">
          <Tooltip text="Annualized value of the last price paid">
            {handlePrice(Number(value))}
          </Tooltip>
        </div>
      ),
    },
  ];

  const rank_data =
    state.rankings &&
    state.rankings.map((item: any) => ({
      rank: item.rank,
      entity_name: item.entity_name,
      entity_id: item.entity_id,
      public_id: item.public_id,
      image_url: item.image_url,
      countdown: item.countdown,
      annualized_value: item.annualized_value,
    }));

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : (
        <></>
      )}
      <div className="home-page">
        <div className="main row mx-0">
          <div
            className={`overflow-hidden Left col-12 col-lg-7 d-flex justify-content-end position-relative ${
              isAuthenticated ? "home-feed" : ""
            }`}
          >
            <div className="sub-left home-follow z-1">
              {isAuthenticated ? (
                state.feeds.length > 0 ? (
                  <div className="left-slide-panel active">
                    <h3 className="mb-3 mb-md-4 d-block font-500-30 ms-2 ms-md-0">
                      Following Feed
                    </h3>
                    <div className="box-custom">
                      <div className="feed-page-list">
                        {state.feeds
                          .map((item, index) => (
                            <FeedList
                              key={item.id}
                              figId={item.figId}
                              figName={item.figName}
                              content={parseMessage(
                                item.content.split("&")[0],
                                navigate
                              )}
                              date={new Date(item.time)}
                              onClick={() =>
                                navigate(`/${item.public_id}`, {
                                  state: { id: item.figId },
                                })
                              }
                              className={
                                index === 0
                                  ? ""
                                  : "mb-3 pb-2 border-bottom border-white"
                              }
                            />
                          ))
                          .reverse()}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end pe-2">
                      <div className="py-4 rank-link">
                        <Link
                          to="/following"
                          className="text-decoration-none text-white toRight"
                        >
                          Go to Following
                          <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="mb-3 mb-md-4 d-block font-500-30 ms-2 ms-md-0">
                      Following
                    </h3>
                    <div className="py-auto h-100">
                      <NullPageComponent
                        heading1={"You're not following anyone yet."}
                        heading2={
                          "Search above or see what's hot in Rankings below."
                        }
                        heading3={
                          "Click Follow on any person, place, or thing's individual page to keep tabs on the excitement!"
                        }
                        button1={
                          <Button
                            label="Go to Rankings"
                            className="btn"
                            onClick={() => navigate("/rankings")}
                            variant="dark"
                          />
                        }
                      />
                    </div>
                  </>
                )
              ) : (
                <div className="ps-2 d-flex w-100 flex-column justify-content-between h-100 gap-3">
                  <div className="d-block">
                    <h1 className="heading">
                      The world’s most exclusive membership
                    </h1>
                    <p className="content">
                      Become Fan1 and claim a one-of-a-kind connection to who or
                      what you adore most.
                    </p>
                  </div>
                  <div className="mt-5 d-flex w-100">
                    <InputBox
                      onChange={handleInputChange}
                      className="border-8 form-control application-form-control home-page-input font-inter font-500 font-20"
                      placeholder="Email"
                      name="email"
                      value={state.email}
                    />
                    <Button
                      variant="dark"
                      className="border-8 yellowBtn home-page-btn font-20 font-500 ms-3 p-1 px-2 p-md-2 px-md-3"
                      onClick={() => {
                        signup(state.email);
                      }}
                      label={"Sign up"}
                    />
                  </div>
                </div>
              )}
            </div>
            {!isAuthenticated && <div className="home-top-ellipse"></div>}
          </div>
          <div
            className={`col-12 col-lg-5 ${
              !isAuthenticated ? "px-5" : "px-0"
            } mobile-rank`}
          >
            <div
              className="Right"
              style={{ width: !isAuthenticated ? "425px" : "" }}
            >
              <div className="mb-2 font-400-30 line-28">Rankings</div>
              <div className="mobile-background-ellipse"></div>
              <div className={`ranking`}>
                <Table
                  columns={rank_columns}
                  data={rank_data}
                  className="ranking-table home-rank"
                  headClassName="head"
                  rowClassName="ranking-table-data"
                  cellClassName="ranking-td"
                />
              </div>

              <div className="d-flex justify-content-end pe-2">
                <div className="pb-3 pt-2 rank-link">
                  <Link
                    to="/rankings"
                    className="text-decoration-none text-white toRight"
                  >
                    See All Rankings
                    <i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="d-none fam-div-2 bg-midnight p-0 position-relative d-md-flex mt-80">
            <div className="col-md-7 d-flex flex-column justify-content-around p-60-80">
              <h2 className="font-500-36">
                Own the moment. Make the connection.
              </h2>
              <h3 className="text-light-purple mb-5 font-500-26">
                Fan1 is where the most passionate fans and the world’s most
                admired connect one-on-one. Our unique approach guarantees
                connection by only allowing one fan at a time. Become Fan1
                today.
              </h3>
              <Button
                className="border-8 yellowBtn font-20 font-500 p-2 px-3 w-40 width-fit"
                onClick={() => {
                  navigate("/available");
                }}
                label={"Browse Available Connections"}
              />
            </div>

            <div className="col-12 col-md-5 ps-1 m-0">
              <img src={Img1} className="fam2" alt="" />
            </div>
          </div>

          <div className="tile-block p-0 mt-80">
            <Tile
              src={mobileImg1}
              alt="Img"
              classname="d-block d-md-none"
              title="Own the moment. Make the connection."
              content="Fan1 is where the most passionate fans and the world’s most admired connect one-on-one. Our unique approach guarantees connection by only allowing one fan at a time. Become Fan1 today."
              contentClass="purple-color font-500-18"
              btnLabel="Browse Available Connections"
              btnClass="yellowBtn p-2 px-3 mt-5 font-500 font-18 w-100"
              onClick={() => {
                navigate("/available");
              }}
            />

            <Tile
              src={Img2}
              alt="Img"
              title="Become Fan1, the ultimate fan experience"
              content="Win the most exclusive connection to the people you adore most. Only one Fan1 at a time—will it be you?"
              btnLabel="Explore How It Works"
              btnClass="whiteBtn p-2 px-3 mt-5 font-500 font-18"
              onClick={() => {
                navigate("/fans");
              }}
            />
            <Tile
              src={Img3}
              alt="Img"
              title="Monetize & engage like never before"
              content="Own the conversation, cut through the noise, and connect with your most dedicated supporters."
              btnLabel="Claim Your Spot"
              btnClass="whiteBtn p-2 px-3 mt-5 font-500 font-18"
              onClick={() => {
                navigate("/famous");
              }}
            />
            <Tile
              src={Img4}
              alt="Img"
              title="Discover the power of Fan1"
              content="Auctions, messaging, rankings—get all the details on how Fan1 works."
              btnLabel="Learn More"
              btnClass="whiteBtn p-2 px-3 mt-5 font-500 font-18"
              onClick={() => {
                navigate("/learn");
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
